import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { bulkEmail, buildings } from '../services/api';

const NOTICE_TYPES = [
  { value: 'general',     label: 'General Notice',     color: 'bg-blue-700',   desc: 'General announcements or updates' },
  { value: 'agm',         label: 'AGM / Meeting',      color: 'bg-purple-700', desc: 'Annual General Meeting or special meeting notices' },
  { value: 'bylaw',       label: 'Bylaw Update',       color: 'bg-red-700',    desc: 'Bylaw changes or enforcement notices' },
  { value: 'fee',         label: 'Fee Notice',         color: 'bg-amber-600',  desc: 'Strata fee changes or payment reminders' },
  { value: 'maintenance', label: 'Maintenance Alert',  color: 'bg-sky-700',    desc: 'Planned maintenance or outage notifications' },
];

export default function BulkMessage() {
  const [buildingList, setBuildingList] = useState([]);
  const [form, setForm] = useState({
    building_id:  '',
    subject:      '',
    message:      '',
    notice_type:  'general',
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    buildings.list().then((res) => setBuildingList(res.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const payload = { ...form, building_id: form.building_id || null };
      const res = await bulkEmail.send(payload);
      setResult(res.data);
      toast.success(`Sent to ${res.data.sent} owner${res.data.sent !== 1 ? 's' : ''}`);
      setForm((f) => ({ ...f, subject: '', message: '' }));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const selectedType = NOTICE_TYPES.find((t) => t.value === form.notice_type) || NOTICE_TYPES[0];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Bulk Owner Message</h1>
        <p className="text-gray-500 text-sm mt-1">Send notices to all owners in one or all buildings.</p>
      </div>

      <form onSubmit={handleSend} className="space-y-5">
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">

          {/* Building selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Building</label>
            <select
              value={form.building_id}
              onChange={(e) => set('building_id', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Buildings</option>
              {buildingList.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Notice type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notice Type</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {NOTICE_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => set('notice_type', t.value)}
                  className={`text-left p-3 rounded-lg border-2 transition-all text-sm ${
                    form.notice_type === t.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}>
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${t.color} mr-2`} />
                  <span className="font-medium text-gray-800">{t.label}</span>
                  <p className="text-xs text-gray-500 mt-1 ml-4.5">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => set('subject', e.target.value)}
              placeholder={`e.g. ${selectedType.label} — Maple Gardens`}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Message body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
            <textarea
              value={form.message}
              onChange={(e) => set('message', e.target.value)}
              rows={8}
              placeholder="Write your notice here. Line breaks and paragraphs are preserved."
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              required
            />
            <p className="text-xs text-gray-400 mt-1">
              Each owner will receive this email with their name personalised in the greeting.
            </p>
          </div>
        </div>

        {/* Preview banner */}
        <div className={`rounded-xl overflow-hidden border border-gray-200`}>
          <div className={`${selectedType.color} px-6 py-4`}>
            <p className="text-white font-semibold text-base">{form.subject || 'Your subject line'}</p>
            <p className="text-white/70 text-sm mt-0.5">
              {form.building_id
                ? buildingList.find((b) => String(b.id) === form.building_id)?.name || ''
                : 'All Buildings'}
            </p>
          </div>
          <div className="bg-gray-50 px-6 py-4">
            <p className="text-sm text-gray-500">Dear Owner,</p>
            <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap line-clamp-3">
              {form.message || 'Your message will appear here…'}
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={sending}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-lg text-sm disabled:opacity-60 transition-colors">
            {sending ? 'Sending…' : 'Send to All Owners'}
          </button>
        </div>
      </form>

      {/* Result */}
      {result && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <svg className="w-6 h-6 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="font-semibold text-green-800">Message sent successfully</h3>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-2xl font-bold text-green-700">{result.sent}</p>
              <p className="text-xs text-gray-500 mt-0.5">Delivered</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className="text-2xl font-bold text-gray-700">{result.total_recipients}</p>
              <p className="text-xs text-gray-500 mt-0.5">Recipients</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <p className={`text-2xl font-bold ${result.failed > 0 ? 'text-red-600' : 'text-gray-700'}`}>{result.failed}</p>
              <p className="text-xs text-gray-500 mt-0.5">Failed</p>
            </div>
          </div>
          {result.failed_details?.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-red-600 cursor-pointer">View failed deliveries</summary>
              <ul className="mt-2 text-xs text-red-700 space-y-0.5">
                {result.failed_details.map((f, i) => (
                  <li key={i}>Unit {f.unit}: {f.email} — {f.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
